export const SCA_ACTION_URL_KEY = 'sca:actionUrl';

// Client-side freshness hint ONLY (link may already be expired server-side)
const DEFAULT_TTL_MS = 9.5 * 60 * 1000; // 9 minutes 30 seconds

const RID_KEY = 'sca_bank_recipient_id';
const RID_TS_KEY = 'sca_bank_recipient_id_ts';

const RESUME_FAIL_KEY = 'bankSetup|resume_failed';
// Soft cooldown when the server says "resume_failed"
const FAIL_COOLDOWN_MS = 60 * 1000; // 1 min

export function clearResumeFailed() {
  try {
    sessionStorage.removeItem(RESUME_FAIL_KEY);
  } catch {
    /* ignore */
  }
}

export function markResumeFailed() {
  try {
    sessionStorage.setItem(RESUME_FAIL_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function canAttemptAutoResume() {
  try {
    const t = Number(sessionStorage.getItem(RESUME_FAIL_KEY) || '0');
    if (!t) return true;
    return Date.now() - t > FAIL_COOLDOWN_MS;
  } catch {
    return true;
  }
}

//
// Legacy recipient ID storage getters/setters/clearers (used in SCA flow)
//

export const setStoredRid = (rid) => {
  try {
    if (rid) {
      sessionStorage.setItem(RID_KEY, rid);
      sessionStorage.setItem(RID_TS_KEY, String(Date.now()));
    }
  } catch {
    /* ignore */
  }
};

export const getStoredRid = () => {
  try {
    return sessionStorage.getItem(RID_KEY);
  } catch {
    return null;
  }
};

export const clearStoredRid = () => {
  try {
    sessionStorage.removeItem(RID_KEY);
    sessionStorage.removeItem(RID_TS_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Append ?returnUrl=<returnUrl> to actionUrl safely.
 * - Preserves existing query + hash
 * - Overwrites existing returnUrl if already there
 * - Encodes properly
 */
export const appendReturnUrl = (actionUrl, returnUrl) => {
  if (!actionUrl || !returnUrl) return null;
  try {
    // URL() requires an absolute URL; if actionUrl might be relative, fall back to window.location.origin
    const base =
      actionUrl.startsWith('http://') || actionUrl.startsWith('https://')
        ? undefined
        : window.location.origin;
    const u = new URL(actionUrl, base);
    u.searchParams.set('returnUrl', returnUrl);
    return u.toString();
  } catch {
    return null;
  }
};

// Remove Mangopay callback params from a URL we plan to use as returnUrl
export function sanitizeReturnUrl(urlLike, extraKeys = []) {
  try {
    const u = new URL(urlLike, window.location.origin);
    const keys = ['controlStatus', 'actionStatus', ...extraKeys];
    keys.forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return urlLike || `${window.location.origin}${window.location.pathname}`;
  }
}

// We store res.sca_bank_action_url in sessionStorage to support retry
// after an external SCA redirect. These URLs are only valid for 10 minutes.
// If expired, getStoredScaActionUrl() returns null and no retry is attempted.
export function clearStoredScaActionUrl() {
  try {
    sessionStorage.removeItem(SCA_ACTION_URL_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredScaActionUrl() {
  try {
    const raw = sessionStorage.getItem(SCA_ACTION_URL_KEY);
    if (!raw) return { url: null, expired: false };

    const record = JSON.parse(raw);
    const isExpired = !record.expiresAt || Date.now() > record.expiresAt;
    const isMalformed = !record.url;

    if (isExpired) {
      return { url: null, expired: true };
    }

    if (isMalformed) {
      return { url: null, expired: false };
    }

    return { url: record.url, expired: false };
  } catch (err) {
    // console.warn('Failed to parse stored SCA actionUrl:', err);
    return { url: null, expired: false };
  }
}

export function setStoredScaActionUrl(url, ttl = DEFAULT_TTL_MS) {
  const record = {
    url,
    expiresAt: Date.now() + ttl,
  };
  try {
    sessionStorage.setItem(SCA_ACTION_URL_KEY, JSON.stringify(record));
  } catch (err) {
    // console.warn('Failed to store SCA actionUrl:', err);
  }
}

export const shouldTriggerSCA = (formData, userData) => {
  return (
    userData.mango_user_id !== null &&
    formData?.username !== undefined &&
    formData.username !== userData.username
  );
};
